import { Injectable } from '@angular/core';
import { Asset, AssetDetail, SearchAsset } from 'src/app/modules/standard/v1';

export enum PreviewMode {
  Thumbnail = 'thumbnail',
  Medium = 'medium',
  Full = 'full'
}

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  constructor() { }

  private defaultImageUrl = './assets/images/default-image.png';
  private fileTypeMap: { [key: string]: string } = {
    'application/pdf': './assets/images/pdf.png',
    'audio': './assets/images/mp3.jpg',
    'video': './assets/images/mp4.png',
    'text': './assets/images/text.png',
  };

  private formatUrl(webUrl: string, mode: PreviewMode, isCacheBust = false): string {
    if (!webUrl) {
      return this.defaultImageUrl;
    }

    const hasQueryParams = webUrl.includes('?');
    const queryParams = `${hasQueryParams ? '&impolicy=thumb' : '?impolicy=thumb'}${mode === PreviewMode.Thumbnail ? '&imwidth=120' : '&imwidth=240'}`;
    const previewUrl = `${webUrl}${queryParams}`;
    return isCacheBust ? this.withImageCacheBust(previewUrl) : previewUrl;
  }

  private resolveFileType(fileType: string): string {
    for (const key in this.fileTypeMap) {
      if (fileType.startsWith(key)) {
        return this.fileTypeMap[key];
      }
    }
    return this.defaultImageUrl;
  }

  private getPreviewUrl(asset: { fileType: string; webUrl?: string }, mode: PreviewMode, isCacheBust = false): string {
    if (!asset.fileType) {
      return this.defaultImageUrl;
    }

    if (asset.fileType.startsWith('image/')) {
      return this.formatUrl(asset.webUrl ?? '', mode, isCacheBust);
    }

    return this.resolveFileType(asset.fileType);
  }

  public getAzureAssetFilePreviewUrl(asset: SearchAsset, mode: PreviewMode = PreviewMode.Thumbnail, isCacheBust = false): string {
    let currentMode = asset.fileType;
    if (asset.fileType?.includes('x-zip-compressed')) {
      currentMode = asset?.zipType == 'Video' ? 'video' : '';
    }
    return this.getPreviewUrl({ fileType: currentMode ?? '', webUrl: asset?.webUrl ?? '' }, mode, isCacheBust);
  }

  public getFilePreviewUrl(asset: Asset, mode: PreviewMode = PreviewMode.Thumbnail, isCacheBust = false): string {
    let currentMode = asset.Detail.FileType;
    if (asset.Detail.FileType?.includes('x-zip-compressed')) {
      currentMode = asset.Detail.ZipType == 'Video' ? 'video' : '';
    }
    return this.getPreviewUrl({ fileType: currentMode ?? '', webUrl: asset?.Detail?.WebUrl ?? '' }, mode, isCacheBust);
  }

  public getOfferPreviewUrl(facingAsset: AssetDetail, mode: PreviewMode = PreviewMode.Thumbnail, isCacheBust = false): string {
    return this.getPreviewUrl({ fileType: facingAsset?.FileType ?? '', webUrl: facingAsset?.WebUrl ?? '' }, mode, isCacheBust);
  }

  public getSearchFilePreviewUrl(asset: SearchAsset, mode: PreviewMode = PreviewMode.Thumbnail, isCacheBust = false): string {
    return this.getPreviewUrl({ fileType: asset?.fileType ?? '', webUrl: asset?.webUrl ?? '' }, mode, isCacheBust);
  }

  public withImageCacheBust(url: string): string {
    if (!url) {
      return url;
    }

    try {
      const parsed = new URL(url);
      parsed.searchParams.set('v', Date.now().toString());
      return parsed.toString();
    } catch {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}v=${Date.now()}`;
    }
  }
}
